// PrimeVue FocusTrap null guard — a bootstrap patch (no npm package, no
// node_modules edit).
//
// THE CRASH
// primevue/focustrap's bind() observes the trapped element with
// `new MutationObserver(cb)` + `{ childList: true }`. For every childList record,
// when focus is OUTSIDE the trap, cb runs
// `focus(findNextFocusableElement(mutation.nextSibling))`, and that helper reads
// `_el.nextSibling` with no null check. A record whose nextSibling is null — a
// node appended after the trap's own trailing hidden sentinel <span> — throws
//   TypeError: Cannot read properties of null (reading 'nextSibling')
// Trigger (seen in Rollbar): foreign code such as a browser extension or password
// manager appends into an open Dialog while focus has left it — e.g. a `:loading`
// PrimeVue Button becomes disabled and Chrome blurs it to <body>. Verified in the
// installed PrimeVue 4.5.5; the identical code ships in PrimeVue 5.0.2, so
// upgrading does not fix it.
//
// THE FIX
// For such records the original callback either does nothing (focus inside the
// trap) or throws (focus outside), so dropping them before they reach PrimeVue's
// callback is behaviour-neutral; every other record is forwarded unchanged. The one
// difference: originally a throw on one record aborted the rest of that batch's
// forEach, whereas now the remaining records are still processed, which is what
// PrimeVue intends. We do it by swapping window.MutationObserver for a filtering
// subclass only for the duration of focustrap's synchronous bind() call, so the
// only observer affected is the one focustrap creates there.
//
// WHY NOT A GLOBAL `app.directive('focustrap', …)` OVERRIDE
// Dialog (and Popover, Drawer, ConfirmPopup, DataTable, Galleria, Image) register
// FocusTrap component-locally (`directives: { focustrap: FocusTrap }`), so a
// global directive never applies to them. Instead we wrap a hook on the shared
// object exported by 'primevue/focustrap': Vue reads `binding.dir[hook]` on every
// call, so the wrapper takes effect for every component that uses it.
//
// WHY AT BOOTSTRAP, AND WHY VIA `created`
// `bind` is not on the exported object. BaseDirective._extend
// (@primevue/core/basedirective) keeps `methods` in a closure and copies them onto
// a per-element instance (`el._$instances.focustrap`) at that element's FIRST hook
// (`created`); later hooks re-spread the previous instance, so an own-property
// override of `bind` set right after `created` survives until `mounted` calls
// `this.bind(el, binding)`. The `created` wrapper must therefore be in place before
// the first trapped element is created, i.e. installed at app bootstrap.
//
// REMOVE WHEN
// PrimeVue's focustrap guards `mutation.nextSibling` (or `_el`) against null
// itself — check bind() in node_modules/primevue/focustrap/index.mjs after each
// PrimeVue upgrade — then delete this file, its test, and the app.use() in main.js.
// Until then, re-run primevueFocusTrapGuard.test.js after every PrimeVue upgrade:
// if a future version re-copies directive methods on later hooks, the `bind`
// override is silently replaced and the guard stops applying without any error.

import FocusTrap from 'primevue/focustrap';

const LOG_PREFIX = '[FocusTrapGuard]';

// Marks our wrappers so neither install() nor the per-element patch ever
// double-wraps. Symbol.for (global registry) keeps the marker shared even if
// this module is evaluated twice (e.g. HMR).
const GUARDED = Symbol.for('crafter.primevueFocusTrapGuard');

let warnedMissingBind = false;

/**
 * Builds a MutationObserver subclass that drops childList records whose
 * nextSibling is null (the ones PrimeVue's focustrap callback crashes on) and
 * forwards every other record, unchanged, to the original callback.
 *
 * @param {typeof MutationObserver} NativeMutationObserver
 * @returns {typeof MutationObserver}
 */
const createGuardedMutationObserver = (NativeMutationObserver) =>
  class GuardedMutationObserver extends NativeMutationObserver {
    constructor(callback) {
      super((records, observer) => {
        const kept = records.filter(
          (record) => !(record.type === 'childList' && record.nextSibling === null),
        );
        if (kept.length) callback.call(observer, kept, observer);
      });
    }
  };

/**
 * Wraps this element's focustrap instance bind() so the MutationObserver it
 * creates is the guarded subclass. The native constructor is restored in
 * `finally`, so the swap never outlives that synchronous call.
 *
 * @param {HTMLElement} el - element carrying the focustrap directive
 */
function guardInstanceBind(el) {
  const instance = el?._$instances?.focustrap;
  if (typeof instance?.bind !== 'function') {
    if (!warnedMissingBind) {
      warnedMissingBind = true;
      Console.warn(`${LOG_PREFIX} focustrap instance has no bind(); trap left unguarded`);
    }
    return;
  }
  if (instance.bind[GUARDED]) return;

  const originalBind = instance.bind;
  const guardedBind = function (...args) {
    const NativeMutationObserver = window.MutationObserver;
    window.MutationObserver = createGuardedMutationObserver(NativeMutationObserver);
    try {
      return originalBind.apply(this, args);
    } finally {
      window.MutationObserver = NativeMutationObserver;
    }
  };
  guardedBind[GUARDED] = true;
  instance.bind = guardedBind;
}

export default {
  install() {
    if (typeof FocusTrap?.created !== 'function') {
      Console.warn(`${LOG_PREFIX} PrimeVue FocusTrap has an unexpected shape; guard not installed`);
      return;
    }
    if (FocusTrap.created[GUARDED]) return; // already installed

    const originalCreated = FocusTrap.created;
    const guardedCreated = function (...args) {
      const result = originalCreated.apply(this, args);
      guardInstanceBind(args[0]);
      return result;
    };
    guardedCreated[GUARDED] = true;
    FocusTrap.created = guardedCreated;
  },
};
